import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getInsights } from "../lib/api";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";

export default function Insights() {
  const [data, setData] = useState({ results: [], categories: [] });
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    getInsights(category === "All" ? undefined : category)
      .then(setData)
      .catch(() => setData({ results: [], categories: [] }))
      .finally(() => setLoading(false));
  }, [category]);

  const [featured, ...rest] = data.results;

  return (
    <main className="pt-[72px]" data-testid="insights-page">
      <section className="border-b border-ivory/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading
            eyebrow="Market Insights"
            title={<>The landed market,<br />read properly.</>}
            description="Articles below are clearly-labelled editorial placeholders demonstrating the insights structure. Production content — with verified URA data, dates and sources — will be published by Andy's team through the CMS."
          />
          <div className="mt-10 flex flex-wrap gap-2" data-testid="insights-categories">
            {["All", ...data.categories].map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`insights-category-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`px-5 py-2.5 text-[0.7rem] font-semibold uppercase tracking-[0.18em] transition-colors duration-300 ${
                  category === c ? "bg-brand text-ivory" : "border border-ivory/20 text-ivory/70 hover:border-ivory hover:text-ivory"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-14 lg:py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          {loading && <p className="text-sm text-ivory/50">Loading editorial…</p>}

          {!loading && featured && (
            <Reveal>
              <Link to={`/insights/${featured.slug}`} data-testid="insights-featured" className="group grid gap-8 border border-ivory/10 bg-[#242424] lg:grid-cols-2">
                <div className="relative aspect-[16/10] overflow-hidden lg:aspect-auto lg:min-h-[380px]">
                  <img src={featured.image} alt={featured.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                  <span className="absolute left-4 top-4 bg-brand px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-ivory">Featured</span>
                </div>
                <div className="flex flex-col justify-center p-8 lg:p-12">
                  <p className="eyebrow text-ivory/60">{featured.category}</p>
                  <h2 className="mt-4 font-serif text-3xl leading-tight text-ivory transition-colors group-hover:text-ivory/80 lg:text-4xl">{featured.title}</h2>
                  <p className="mt-4 text-sm leading-relaxed text-ivory/60">{featured.excerpt}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-ivory">
                    Read the piece <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                  <span className="mt-4 text-[0.65rem] uppercase tracking-[0.2em] text-ivory/40">Editorial placeholder</span>
                </div>
              </Link>
            </Reveal>
          )}

          <Stagger className="mt-10 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((a) => (
              <StaggerItem key={a.slug}>
                <Link to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`} className="group block border border-ivory/10 bg-[#242424] transition-shadow duration-500 hover:shadow-[0_20px_40px_rgba(0,0,0,0.45)]">
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <img src={a.image} alt={a.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" />
                    <span className="absolute left-4 top-4 border border-ivory/20 bg-charcoal/80 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.2em] text-ivory backdrop-blur-sm">{a.category}</span>
                  </div>
                  <div className="p-7">
                    <h3 className="font-serif text-2xl leading-snug text-ivory transition-colors duration-300 group-hover:text-ivory/80">{a.title}</h3>
                    <p className="mt-3 text-sm leading-relaxed text-ivory/60">{a.excerpt}</p>
                    <span className="mt-4 block text-[0.65rem] uppercase tracking-[0.2em] text-ivory/40">Editorial placeholder</span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>

          <p className="mt-10 max-w-3xl text-xs leading-relaxed text-ivory/40" data-testid="insights-disclaimer">
            No article on this page reports live market data. Production publishing workflow: Andy's PA drafts in the
            CMS, Andy reviews for accuracy and compliance, then publishes — no design edits needed per article.
          </p>
        </div>
      </section>
    </main>
  );
}
